import React, { useState } from 'react';
import { apiFetch } from '../apiClient';
import { todayDateInput } from './registerFormat';

// Suggestions only, not a closed vocabulary (schema.prisma's ProjectEvent.eventType comment) -- the
// <datalist> below lets a PM pick one or type anything.
export const PROJECT_EVENT_TYPE_PRESETS = [
  'Project Meeting', 'Stakeholder Meeting', 'Blackout Period', 'Decision Deadline', 'Executive Review', 'Other',
];

export function blankEventDraft(projectId) {
  return {
    projectId,
    title: '',
    eventType: '',
    startDate: todayDateInput(),
    endDate: '',
    internalOnly: false,
    notes: '',
  };
}

/**
 * "+ Add Project Event", shared by the Project Details 30/60/90 frame (ProjectLaunch.js) and Project
 * Calendar (ProjectCalendar.js) -- both need the identical create form, so it lives once here rather
 * than as two copies that could drift on validation or fields.
 *
 * `projects` is the set of projects the caller can log against: one entry with no picker shown
 * (Project Details, always its own project) or several (Project Calendar, whichever are selected) --
 * an event needs exactly one project, so a picker only appears when there's a real choice.
 *
 * `portal` hides "Internal only" -- a canEdit customer creating an event from CustomerProjectCalendar.js
 * has no use for a control whose whole purpose is hiding the row from the customer portal, and
 * ProjectEventsService rejects them setting it anyway (staff-only, same as RAAIDD's internalOnly).
 */
export default function ProjectEventModal({ projects, initialProjectId, onClose, onSaved, portal = false }) {
  const [draft, setDraft] = useState(() => blankEventDraft(initialProjectId || projects[0]?.id || ''));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  function set(key) {
    return (e) => setDraft((prev) => ({ ...prev, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  }

  const canSave = draft.projectId && draft.title.trim() && draft.eventType.trim() && draft.startDate;

  async function save() {
    setSaving(true);
    setError('');
    try {
      await apiFetch('/project-events', {
        method: 'POST',
        body: JSON.stringify({
          projectId: draft.projectId,
          title: draft.title.trim(),
          eventType: draft.eventType.trim(),
          startDate: draft.startDate,
          endDate: draft.endDate || undefined,
          internalOnly: draft.internalOnly,
          notes: draft.notes.trim() || undefined,
        }),
      });
      await onSaved?.();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.35)' }} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="add-project-event-title"
        className="card"
        style={{
          position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          zIndex: 41, width: 460, maxWidth: 'calc(100vw - 24px)', marginBottom: 0,
          boxShadow: '0 10px 34px rgba(0,0,0,0.28)',
        }}
      >
        <div id="add-project-event-title" className="card-title">New Project Event</div>
        {error && <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 10 }}>⚠ {error}</div>}

        {projects.length > 1 && (
          <div className="form-group">
            <label className="form-label">Project</label>
            <select className="form-select" value={draft.projectId} onChange={set('projectId')}>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        )}
        <div className="form-group">
          <label className="form-label">Title</label>
          <input className="form-input" value={draft.title} onChange={set('title')} />
        </div>
        <div className="form-group">
          <label className="form-label">Event Type</label>
          <input className="form-input" list="project-event-type-presets" value={draft.eventType} onChange={set('eventType')} />
          <datalist id="project-event-type-presets">
            {PROJECT_EVENT_TYPE_PRESETS.map((t) => <option key={t} value={t} />)}
          </datalist>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="form-group">
            <label className="form-label">Start Date</label>
            <input type="date" className="form-input" value={draft.startDate} onChange={set('startDate')} />
          </div>
          <div className="form-group">
            <label className="form-label">End Date (optional)</label>
            <input type="date" className="form-input" value={draft.endDate} onChange={set('endDate')} />
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Notes</label>
          <textarea className="form-input" rows={2} value={draft.notes} onChange={set('notes')} />
        </div>
        {!portal && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-mid)', cursor: 'pointer', marginBottom: 16 }}>
            <input type="checkbox" checked={draft.internalOnly} onChange={set('internalOnly')} />
            <strong>Internal only</strong> — hide this event from the customer portal
          </label>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={!canSave || saving}>
            {saving ? 'Saving…' : 'Add event'}
          </button>
        </div>
      </div>
    </>
  );
}
